import React from 'react'
import AuthenticatedPage from 'containers/AuthenticatedPage'
import { getMessage } from 'lib/translator'
import {
  Input,
  SingleDatePicker,
  Select,
  Upload,
  Toggle,
} from 'components/Form'
import { Popup } from 'components/Popup'
import { formatISO } from 'date-fns'
import API from 'lib/api'
import './style.scss'
import {
  dropDownOptions,
  requiredFields,
  initialGameValue,
  dateAndTimeDeconstructor,
  reducer,
  handleError,
  getUpdateGameBody,
  getAddGameBody,
  copyGameDeepLink,
} from './utils'
import { getActualDimension } from 'components/AppHome/AppSplash/utils'
import { withRouter } from 'react-router-dom'
import { getUserRoles } from 'lib/auth'

const GameSettingsForm = (props) => {
  const isEdit = window.location.href.includes('edit')
  const getPageTitle = isEdit
    ? getMessage('games.title.edit')
    : getMessage('games.title.add')
  const [gameState, dispatch] = React.useReducer(reducer, initialGameValue)
  const [showPopup, setShowPopup] = React.useState(false)
  const [error, setError] = React.useState('')
  const [errorMsg, setErrorMsg] = React.useState('')
  const [hasCtaButton, setHasCtaButton] = React.useState(false)
  const gameId = props.router.match.params.id
  const isReadOnly = getUserRoles().includes('Marketing / Games (readonly)')
  const nameCharMaxLimit = 22
  const descriptionCharMaxLimit = 54

  async function validateDimension() {
    const bannerImage = 'banner_image'
    const expectedRatio = '2.5'
    const expectedMinWidth = 656
    const expectedMinHeight = 262.4
    const { actualWidth, actualHeight } = await getActualDimension(
      gameState.bannerImage
    )
    if (actualWidth === 0 || actualHeight === 0) {
      showError('Invalid image')
      handleInputChange(bannerImage, '') // clear image as it's invalid
    }
    const ratio = actualWidth / actualHeight
    const widthToHeightRatio = ratio % 1 !== 0 ? ratio.toFixed(1) : ratio
    if (widthToHeightRatio !== expectedRatio) {
      showError(
        `Width to Height ratio should be 2.5:1. Uploaded image ratio is ${widthToHeightRatio}:1. (${actualWidth} x ${actualHeight})`
      )
      handleInputChange(bannerImage, '') // clear image as it's invalid
    } else if (
      expectedMinHeight > actualHeight ||
      expectedMinWidth > actualWidth
    ) {
      showError(
        `Invalid image size. The image should have minimum 656px x 262.4px (width x height). Upload image is ${actualWidth}px x ${actualHeight}px`
      )
      handleInputChange(bannerImage, '') // clear image as it's invalid
    }
  }

  React.useEffect(() => {
    if (isEdit) {
      const url = `/gamification-service/v1/games/${gameId}`
      const getGameByIdApi = new API({ url })
      getGameByIdApi
        .get()
        .then((res) => {
          const { startAt, endAt, name, uuid, voucherConfig } = res.data
          const deconstructStartAt = dateAndTimeDeconstructor(startAt)
          const deconstructEndAt = dateAndTimeDeconstructor(endAt)
          const gameData = {
            name: name,
            gameDeepLink: uuid,
            startDate: deconstructStartAt.date,
            startTime: deconstructStartAt.time,
            endDate: deconstructEndAt.date,
            endTime: deconstructEndAt.time,
            providers: res.data.config.provider.name,
            providerGameUrl: res.data.config.provider.url,
            ruleCampaignUid: res.data.config.ruleCampaign.id,
            maxGameLimit: res.data.rules[0]?.value.maxPerDay,
            gameRules: res.data.rules,
            bannerImage: res.data.config.metadata.bannerImage,
            description: res.data.config.metadata.description,
            isVisible: res.data.config.isVisible,
            gameRewardText: voucherConfig?.message,
            secBtnGameCtaText: voucherConfig?.secBtnText,
            secBtnGameCtaDeepLink: voucherConfig?.secBtnDeeplink,
            secBtnEnabled: voucherConfig?.secBtnEnabled,
          }
          setHasCtaButton(voucherConfig?.secBtnEnabled)
          dispatch({ type: 'fetch_edit', value: gameData })
        })
        .catch((err) => {
          handleError({
            setShowPopup,
            setError,
            setErrorMsg,
            errMessage: err.message,
          })
        })
    }
  }, [])

  React.useEffect(() => {
    if (gameState.bannerImage) {
      validateDimension()
    }
  }, [gameState.bannerImage])

  const handleInputChange = (type, event) => {
    dispatch({ type, value: event })
  }

  const showError = (message) => {
    handleError({
      setShowPopup,
      setError,
      setErrorMsg,
      errMessage: message,
    })
  }

  const onCtaCheckboxChange = () => {
    const nextState = !hasCtaButton
    dispatch({ type: 'sec_btn_enabled', value: nextState })
    if (nextState === false) {
      dispatch({ type: 'game_cta_text', value: '' })
      dispatch({ type: 'game_cta_link', value: '' })
    }
    setHasCtaButton(nextState)
  }

  const handleClickSubmit = () => {
    const url = `/gamification-service/v1/games`
    const getStartAt = new Date(`${gameState.startDate} ${gameState.startTime}`)
    const getEndAt = new Date(`${gameState.endDate} ${gameState.endTime}`)

    for (let i = 0; i < requiredFields.length; i++) {
      if (
        !gameState[requiredFields[i]] ||
        gameState[requiredFields[i]].length === 0
      ) {
        showError(`${requiredFields[i]} is required`)
        return
      }
    }

    if (gameState.name.length > nameCharMaxLimit) {
      showError('Name cannot be longer than 22 characters')
      return
    }

    if (getStartAt.valueOf() >= getEndAt.valueOf()) {
      showError('Start date must be earlier than end date')
      return
    }

    if (gameState.description.length > descriptionCharMaxLimit) {
      showError('Description cannot be longer than 54 characters')
      return
    }

    if (
      hasCtaButton &&
      (gameState.secBtnGameCtaText.length === 0 ||
        gameState.secBtnGameCtaDeepLink.length === 0)
    ) {
      showError('Cta text and Cta deep link are both required')
      return
    }

    if (gameState.gameRewardText?.length === 0 || !gameState.gameRewardText) {
      showError('Custom reward text is required')
      return
    }

    const startAt = formatISO(getStartAt)
    const endAt = formatISO(getEndAt)
    const body = getAddGameBody(gameState, startAt, endAt)

    if (isEdit) {
      const updateGameApi = new API({ url: `${url}/${gameId}` })
      const updateBody = getUpdateGameBody(body, gameState)
      updateGameApi
        .patch(updateBody)
        .then((res) => {
          if (res.code === 200) {
            setShowPopup(true)
          }
        })
        .catch((err) => {
          handleError({
            setShowPopup,
            setError,
            setErrorMsg,
            errMessage: err.message,
          })
        })
    } else {
      const createNewGameApi = new API({ url })
      createNewGameApi
        .post(body)
        .then((res) => {
          if (res.code === 201) {
            handleInputChange('cancel')
            setShowPopup(true)
          }
        })
        .catch((err) => {
          handleError({
            setShowPopup,
            setError,
            setErrorMsg,
            errMessage: err.message,
          })
        })
    }
  }

  return (
    <AuthenticatedPage
      menu={props.menu}
      from={props.location && props.location.pathname}
    >
      <div className="flex-around game-settings-heading">
        <h1 className="game-form-title">{getPageTitle}</h1>
        <div className="visible-status">
          <span
            className={`status-text ${gameState.isVisible ? 'visible' : 'not-visible'}`}
          >
            {gameState.isVisible
              ? getMessage('games.visible.text')
              : getMessage('games.hidden.text')}
          </span>
          <Toggle
            name="gameVisibility"
            value={gameState.isVisible}
            icons={false}
            onChange={(event) => handleInputChange('is_visible', event)}
          />
        </div>
      </div>
      <div className="game-form-fields">
        <div className="field-row">
          <Input
            label={`${getMessage('games.input.gameName.label')} (${nameCharMaxLimit} char)`}
            placeholder={getMessage('games.input.gameName.placeholder')}
            value={gameState.name}
            onChange={(event) => handleInputChange('game_name', event)}
            name="game-name"
          />
          {isEdit && (
            <div className="deeplink-row">
              <Input
                label={getMessage('games.input.gameDeepLink.label')}
                placeholder={getMessage('games.input.gameDeepLink.placeholder')}
                value={`fairprice://gamification/start?gameId=${gameState.gameDeepLink}`}
                disabled={true}
                name="game-deeplink"
                id="game-deeplink"
              />
              <div style={{ margin: 'auto' }}>
                <button onClick={copyGameDeepLink}>&#x2398;</button>
              </div>
            </div>
          )}
        </div>

        <div className="field-row">
          <Upload
            name="image"
            className="bannerImage"
            placeholder={getMessage('games.bannerImage.placeholder')}
            label={getMessage('games.bannerImage.label')}
            accept={'image/jpeg,image/jpg,image/png,image/gif'}
            value={gameState.bannerImage}
            required
            onChange={(event) => handleInputChange('banner_image', event)}
          />
        </div>

        <div className="single-input-field">
          <Input
            label={`${getMessage('games.description.label')} (${descriptionCharMaxLimit} char)`}
            placeholder={getMessage('games.description.placeholder')}
            value={gameState.description}
            onChange={(event) => handleInputChange('description', event)}
            required
            name="description"
          />
        </div>

        <div className="field-row">
          <div className="start">
            <SingleDatePicker
              enableToday
              label={getMessage('games.startDate.label')}
              value={gameState.startDate}
              onChange={(event) => handleInputChange('start_date', event)}
              name="start-date"
              style={{ width: '15rem' }}
            />
            <div className="time-wrapper">
              <label htmlFor="start-time">
                {getMessage('games.startTime.label')}
              </label>
              <input
                className="time-input"
                value={gameState.startTime}
                type="time"
                id="start-time"
                name="start-time"
                required
                step="1"
                onChange={(event) =>
                  handleInputChange('start_time', event.target.value)
                }
              />
            </div>
          </div>
          <div className="end">
            <SingleDatePicker
              enableToday
              label={getMessage('games.endDate.label')}
              value={gameState.endDate}
              onChange={(event) => handleInputChange('end_date', event)}
              name="end-date"
              style={{ width: '15rem' }}
            />
            <div className="time-wrapper">
              <label htmlFor="end-time">
                {getMessage('games.endTime.label')}
              </label>
              <input
                className="time-input"
                value={gameState.endTime}
                type="time"
                id="end-time"
                name="end-time"
                required
                step="1"
                onChange={(event) =>
                  handleInputChange('end_time', event.target.value)
                }
              />
            </div>
          </div>
        </div>
        <div className="field-row">
          <Select
            label={getMessage('games.providers.label')}
            placeholder={getMessage('games.providers.placeholder')}
            options={dropDownOptions}
            value={gameState.providers}
            onChange={(event) => handleInputChange('providers', event)}
            name="provider-name"
          />
        </div>
        <div className="single-input-field">
          <Input
            label={getMessage('games.providerGameUrl.label')}
            placeholder={getMessage('games.providerGameUrl.placeholder')}
            value={gameState.providerGameUrl}
            onChange={(event) => handleInputChange('provider_game_url', event)}
            name="provider-url"
          />
        </div>
        <div className="single-input-field">
          <Input
            label={getMessage('games.ruleCampaignUid.label')}
            placeholder={getMessage('games.ruleCampaignUid.placeholder')}
            value={gameState.ruleCampaignUid}
            onChange={(event) => handleInputChange('rule_campaign_uid', event)}
            name="rule-campaign-uid"
          />
        </div>
        <div className="single-input-field">
          <Input
            label={getMessage('games.maxGameLimit.label')}
            placeholder={getMessage('games.maxGameLimit.placeholder')}
            value={gameState.maxGameLimit}
            onChange={(event) => handleInputChange('max_game_limit', event)}
            type="number"
            name="game-max-limit"
          />
        </div>
        <div className="game-cta-button">
          <div className="game-cta-button-header">
            <h4>{getMessage('games.ctaButton.title')}</h4>
            <input
              id="hasCtaButton"
              type="checkbox"
              onChange={onCtaCheckboxChange}
              value={hasCtaButton}
              checked={hasCtaButton === true}
            />
            <label for="hasCtaButton">
              {getMessage('games.gameCtaText.checkbox')}
            </label>
          </div>
          <div className="cta-button-input">
            <Input
              label={getMessage('games.gameCtaText.label')}
              placeholder={getMessage('games.gameCtaText.placeholder')}
              value={gameState.secBtnGameCtaText}
              onChange={(event) => handleInputChange('game_cta_text', event)}
              name="game-cta-text"
              disabled={!hasCtaButton}
            />
            <Input
              label={getMessage('games.gameCtaDeeplink.label')}
              placeholder={getMessage('games.gameCtaDeeplink.placeholder')}
              value={gameState.secBtnGameCtaDeepLink}
              onChange={(event) => handleInputChange('game_cta_link', event)}
              name="game-cta-deeplink"
              disabled={!hasCtaButton}
            />
          </div>
        </div>
        <div>
          <h4>{getMessage('games.gameRewardText.title')}</h4>
          <div className="single-input-field">
            <Input
              label={getMessage('games.gameRewardText.label')}
              placeholder={getMessage('games.gameRewardText.placeholder')}
              value={gameState.gameRewardText}
              onChange={(event) => handleInputChange('game_reward_text', event)}
              name="game-reward-title"
            />
          </div>
        </div>
        {!isReadOnly && (
          <div className="game-buttons">
            <button className="submit-button" onClick={handleClickSubmit}>
              {getMessage('games.button.submit')}
            </button>
          </div>
        )}
      </div>
      <Popup
        show={showPopup}
        heading={error ? 'Error' : 'Success'}
        close={() => {
          setError(false)
          setShowPopup(false)
          setErrorMsg('')
        }}
      >
        <div>
          {error ? (
            <div style={{ color: 'red' }}>{errorMsg}</div>
          ) : (
            <span className="successCheckmark">Success &#9989;</span>
          )}
        </div>
      </Popup>
    </AuthenticatedPage>
  )
}

export default withRouter(({ location, history, match, ...props }) => (
  <GameSettingsForm router={{ location, history, match }} {...props} />
))
